import React, { useEffect, useMemo } from 'react';
import cn from 'classnames';
import SVG from '@repo/icons/SVGWrapper';
import styles from './UdoTrigger.module.scss';
import { inOutQuad, ptsAtProgress } from '@repo/utils/helpers/animation';
import { alertColorMap } from '@repo/utils/models/SystemAlert';
import { SVGProps, SystemAlertSeverity } from '@repo/utils/types';
import { useTheme } from '@repo/themes';
import { useTween } from '@repo/hooks';
import { useSelector } from 'react-redux';
import { selectUdoMenuSystemAlert } from '@repo/redux/actions/alerts';
import { Overlay, Tooltip } from '@repo/components/tooltips';
import { selectAllActivityNotifications } from '@repo/redux/reducers/data/activityNotifications';

const themeGradientColorMap = {
  light: '#48494A',
  dark: '#fff',
};

const themeGradientOpacityMap = {
  light: 0.4,
  dark: 0.56,
};

const themeFaceOutlineStrokeMap = {
  light: '#494949',
  dark: '#fcfdff',
};

const themeFaceFillColorMap = {
  light: '#000',
  dark: '#fff',
};

const themeFaceFillOpacityMap = {
  light: 0.25,
  dark: 0.5,
};

// Relative coords, i.e. lowercase "c" curve to command points
const eyeOpenPts = [
  // starting point
  { x: 15.7, y: 8.25 },
  // curve 1
  { x: 0.1, y: -0.75 },
  { x: 1.3, y: -0.75 },
  { x: 1.4, y: 0 },
  // curve 2
  { x: 0, y: 0 },
  { x: 0, y: 1.8 },
  { x: 0, y: 1.8 },
  // curve 3
  { x: -0.1, y: 0.75 },
  { x: -1.3, y: 0.75 },
  { x: -1.4, y: 0 },
  // curve 4
  { x: 0, y: 0 },
  { x: 0, y: -1.8 },
  { x: 0, y: -1.8 },
];

const eyeClosedPts = [
  // starting point
  { x: 15.3, y: 8.45 },
  // curve 1
  { x: 0.7, y: -0.3 },
  { x: 1.5, y: -0.3 },
  { x: 2.2, y: 0 },
  // curve 2
  { x: 0.65, y: 0.3 },
  { x: 0.35, y: 1.5 },
  { x: -0.4, y: 1.3 },
  // curve 3
  { x: -0.4, y: -0.2 },
  { x: -1, y: -0.2 },
  { x: -1.4, y: 0 },
  // curve 4
  { x: -0.65, y: 0.2 },
  { x: -1.1, y: -1 },
  { x: -0.4, y: -1.3 },
];

const AlertFaceGlow = ({
  clipPath,
  filter,
  severity,
}: {
  severity: SystemAlertSeverity;
  clipPath: string;
  filter: string;
}) => (
  <circle
    fill={alertColorMap[severity]}
    cx="20"
    cy="4"
    r="3.2"
    clipPath={clipPath}
    filter={filter}
  />
);

interface AlertDotProps {
  filter: string;
}

const LowAlertDot = ({ filter }: AlertDotProps) => {
  const props = { cx: '24.75', cy: '-0.75', r: '2.3' };
  return (
    <g fill={alertColorMap.low} stroke="none">
      <circle filter={filter} {...props} />
      <circle {...props} />
    </g>
  );
};

const MedAlertDot = ({ filter }: AlertDotProps) => {
  const props = { x: '22.4', y: '-2.8', width: '4.6', height: '4.6', rx: '0.6' };
  return (
    <g fill={alertColorMap.medium} stroke="none" transform="rotate(45, 24.7, -0.5)">
      <rect filter={filter} {...props} />
      <rect {...props} />
    </g>
  );
};

const HighAlertDot = ({ filter }: AlertDotProps) => {
  const d = 'M24.7 -3l2.5 4.2h-5z';
  return (
    <g fill={alertColorMap.high} stroke={alertColorMap.high} strokeLinejoin="round" strokeWidth="1">
      <path filter={filter} d={d} />
      <path d={d} />
    </g>
  );
};

const alertDotMap: { [K in SystemAlertSeverity]: ({ filter }: AlertDotProps) => JSX.Element } = {
  low: LowAlertDot,
  medium: MedAlertDot,
  high: HighAlertDot,
};

export function UdoTrigger(props: SVGProps & { isOpen: boolean }) {
  const alert = useSelector(selectUdoMenuSystemAlert);
  const notifications = useSelector(selectAllActivityNotifications);
  const hasNewNotifications = notifications.items.some((notification) => !notification.seen);
  let alertSeverity: SystemAlertSeverity = hasNewNotifications ? 'low' : undefined;

  if (alert) {
    alertSeverity = alert.severity;
  }

  const [_, themeFamily] = useTheme();
  const [animationProgress, resetTween] = useTween({
    startingValue: 0,
    targetValue: 1,
    duration: 0.2,
    easingFn: inOutQuad,
  });

  const id = useMemo(() => String(Math.random()), []);
  const eyeGradID = `eyeGrad_${id}`;
  const noseGradID = `noseGrad_${id}`;
  const mouthGradID = `mouthGrad_${id}`;
  const faceFillID = `faceFill_${id}`;
  const headEllipseID = `head_${id}`;
  const alertFaceGlowID = `alertFaceGlow_${id}`;
  const alertDotBlurID = `alertDotBlur_${id}`;
  const headClipID = `headClip_${id}`;
  const lineMouthGradID = `lineMouthGrad_${id}`;

  const gradientColor = themeGradientColorMap[themeFamily];
  const gradientOpacity = themeGradientOpacityMap[themeFamily];
  const faceOutlineStroke = themeFaceOutlineStrokeMap[themeFamily];
  const eyePts = ptsAtProgress(animationProgress, 0, 1, eyeClosedPts, eyeOpenPts, []);
  const eyePath = `M${eyePts[0].x} ${eyePts[0].y}
                   c${eyePts[1].x} ${eyePts[1].y} ${eyePts[2].x} ${eyePts[2].y} ${eyePts[3].x} ${eyePts[3].y}
                   c${eyePts[4].x} ${eyePts[4].y} ${eyePts[5].x} ${eyePts[5].y} ${eyePts[6].x} ${eyePts[6].y}
                   c${eyePts[7].x} ${eyePts[7].y} ${eyePts[8].x} ${eyePts[8].y} ${eyePts[9].x} ${eyePts[9].y}
                   c${eyePts[10].x} ${eyePts[10].y} ${eyePts[11].x} ${eyePts[11].y} ${eyePts[12].x} ${eyePts[12].y}`;

  const renderMouth = () => {
    if (alertSeverity == 'high') {
      return <path stroke={`url(#${lineMouthGradID})`} d="M10.3 18h3" />;
    }
    return (
      <path
        className={styles.mouth}
        stroke={`url(#${mouthGradID})`}
        d="M7.2 15c 2.6 3.9 7.4 3.9 10 0"
      />
    );
  };

  const AlertDot = alertDotMap[alertSeverity];

  useEffect(() => {
    resetTween(props.isOpen ? 0 : 1);
  }, [props.isOpen]);

  return (
    <Tooltip
      overlay={<Overlay className={styles.udoTooltip}>App menu</Overlay>}
      placement="left"
      delay
    >
      <SVG
        key={themeFamily}
        className={cn(styles.udo, styles[themeFamily], props.isOpen && styles.open)}
        viewBox="-1 -6 31 31"
        width={32}
        height={32}
      >
        <defs>
          <linearGradient
            x1="50%"
            y1="8.2"
            x2="50%"
            y2="10.7"
            gradientUnits="userSpaceOnUse"
            id={eyeGradID}
          >
            <stop stopColor={gradientColor} stopOpacity={gradientOpacity} offset="0%"></stop>
            <stop stopColor={gradientColor} offset="100%"></stop>
          </linearGradient>
          <linearGradient x1="50%" y1="0%" x2="50%" y2="100%" id={noseGradID}>
            <stop stopColor={gradientColor} stopOpacity={gradientOpacity} offset="0%"></stop>
            <stop stopColor={gradientColor} offset="100%"></stop>
          </linearGradient>
          <linearGradient x1="100%" y1="73.7%" x2="43.9%" y2="42.3%" id={mouthGradID}>
            <stop stopColor={gradientColor} stopOpacity={gradientOpacity} offset="0%"></stop>
            <stop stopColor={gradientColor} offset="100%"></stop>
          </linearGradient>
          <radialGradient id={faceFillID} cx="53%" cy="40%" r="70%">
            <stop offset="50%" stopColor={themeFaceFillColorMap[themeFamily]} stopOpacity="0.04" />
            <stop
              offset="105%"
              stopColor={themeFaceFillColorMap[themeFamily]}
              stopOpacity={themeFaceFillOpacityMap[themeFamily]}
            />
          </radialGradient>
          <linearGradient
            x1="9"
            y1="50%"
            x2="13"
            y2="50%"
            gradientUnits="userSpaceOnUse"
            id={lineMouthGradID}
          >
            <stop stopColor={gradientColor} offset="0%"></stop>
            <stop stopColor={gradientColor} stopOpacity={gradientOpacity} offset="100%"></stop>
          </linearGradient>
          <filter
            x="-100.0%"
            y="-100.0%"
            width="300.0%"
            height="300.0%"
            filterUnits="objectBoundingBox"
            id={alertDotBlurID}
          >
            <feGaussianBlur stdDeviation="2" in="SourceGraphic"></feGaussianBlur>
          </filter>
          <filter
            x="-100.0%"
            y="-100.0%"
            width="300.0%"
            height="300.0%"
            filterUnits="objectBoundingBox"
            id={alertFaceGlowID}
          >
            <feGaussianBlur stdDeviation="3.7" in="SourceGraphic"></feGaussianBlur>
          </filter>
          <ellipse id={headEllipseID} cx="12" cy="12" rx="12" ry="11.8" />
          <clipPath id={headClipID}>
            <use href={`#${headEllipseID}`} />
          </clipPath>
        </defs>
        <use
          href={`#${headEllipseID}`}
          className={styles.head}
          fill={`url(#${faceFillID})`}
          stroke="none"
        />
        {!!AlertDot && (
          <AlertFaceGlow
            severity={alertSeverity}
            clipPath={`url(#${headClipID})`}
            filter={`url(#${alertFaceGlowID})`}
          />
        )}
        <ellipse
          opacity="0.1"
          stroke={faceOutlineStroke}
          strokeWidth="0.5"
          cx="12"
          cy="12"
          rx="12"
          ry="11.8"
          fill="none"
        />
        <g
          className={styles.facialFeatures}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.4"
          fill="none"
        >
          <path stroke="none" fill={`url(#${eyeGradID})`} d={eyePath} />
          <path stroke={`url(#${eyeGradID})`} d="M7.4 8.4v1.5" />
          <path stroke={`url(#${noseGradID})`} d="M11.8 8.3l-1.4 5l2.8 0.7" />
          {renderMouth()}
        </g>
        {!!AlertDot && <AlertDot filter={`url(#${alertDotBlurID})`} />}
      </SVG>
    </Tooltip>
  );
}
